import { useState, useEffect, useCallback } from 'react';
import { User, OperatorInfo } from '../types';
import { fetchUsers as fetchUsersRequest } from '../allUsersApi';
import { apiErrorMessage } from '../../reports/edit/editApi';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [operator, setOperator] = useState<OperatorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shouldRefetch, setShouldRefetch] = useState(true);

  const sortUsers = useCallback((usersToSort: User[]): User[] => {
    return [...usersToSort].sort((a, b) => {
      const levelA = a.roleLevel ?? 0;
      const levelB = b.roleLevel ?? 0;

      if (levelA !== levelB) {
        return levelB - levelA; // По убыванию ранга (100 -> 90 -> 80 -> ...)
      }

      const numA = parseInt(a.pharmacyNum) || 0;
      const numB = parseInt(b.pharmacyNum) || 0;
      if (numA !== numB) {
        return numA - numB;
      }

      return a.id - b.id;
    });
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!shouldRefetch) return;

    try {
      // setLoading(true) вызывается ТОЛЬКО если список пользователей пуст (первичная загрузка),
      // чтобы фоновые обновления происходили абсолютно бесшовно без мерцания интерфейса
      setUsers(currentUsers => {
        if (currentUsers.length === 0) {
          setLoading(true);
        }
        return currentUsers;
      });

      setError(null);
      const data = await fetchUsersRequest();
      setOperator(data.operator);
      setUsers(sortUsers(data.users));
      setShouldRefetch(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось загрузить список пользователей'));
    } finally {
      setLoading(false);
    }
  }, [shouldRefetch, sortUsers]);

  const refetchUsers = useCallback(() => {
    setShouldRefetch(true);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    setUsers,
    loading,
    error,
    refetchUsers,
    sortUsers,
    operator
  };
};
